import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { adminApi, CLIENT_ROLES, notSignedIn, ROLE_LABEL, STAFF_ROLES, useAdminMe, type AdminUser, type Group, type Org, type Role } from "../../lib/admin";
import { Drawer, Field, Notice, Pill, Toolbar, clean, fmtDateTime } from "./ui";

export default function Users({ orgs, groups }: { orgs: Org[]; groups: Group[]; superAdmin?: boolean }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [edit, setEdit] = useState<AdminUser | "new" | null>(null);
  const [msg, setMsg] = useState<{ text: string; password?: string; warn?: boolean } | null>(null);
  const [pending, setPending] = useState(false);
  const [sending, setSending] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setUsers(await adminApi<AdminUser[]>("GET", "users")); setErr(null); }
    catch (e: any) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (users ?? []).filter((u) => (!orgFilter || (u.org_ids ?? [u.org_id]).includes(orgFilter)) && (!pending || notSignedIn(u)) &&
      (!s || [u.email, u.full_name, ...(u.org_names ?? [u.org_name]), u.phone].some((v) => v?.toLowerCase().includes(s))));
  }, [users, q, orgFilter, pending]);
  const waiting = (users ?? []).filter(notSignedIn).length;

  const done = async (text: string, password?: string, warn?: boolean) => { setEdit(null); setMsg({ text, password, warn }); await load(); };
  async function resend(u: AdminUser) {
    setSending(u.id); setMsg(null);
    try { setMsg({ text: inviteSentText(u.email, await adminApi<{ sent: string }>("POST", `users/${u.id}/resend-invite`, {})) }); await load(); }
    catch (e: any) { setMsg({ text: e.message, warn: true }); }
    finally { setSending(null); }
  }

  return (
    <section>
      <Toolbar>
        <h1>Users</h1>
        <input className="asearch" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="asearch" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
          <option value="">All companies</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <label className="achk"><input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} /> Not signed in yet ({waiting})</label>
        <button className="btn-primary" onClick={() => setEdit("new")}>+ Add user</button>
      </Toolbar>
      {msg && <TempPasswordNotice {...msg} onClose={() => setMsg(null)} />}
      {err && <Notice kind="bad">{err}</Notice>}
      <div className="atable-wrap">
        <table className="atable">
          <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Role</th><th>Status</th><th>Last sign-in</th><th /></tr></thead>
          <tbody>
            {users === null && !err && <tr className="empty"><td colSpan={7}>Loading…</td></tr>}
            {rows.map((u) => (
              <tr key={u.id} onClick={() => setEdit(u)}>
                <td><b>{u.full_name ?? "—"}</b>{u.is_me && <span className="sub"> (you)</span>}</td>
                <td className="mono">{u.email}</td>
                <td>{u.org_name ?? <span className="sub">No company</span>}{(u.org_names?.length ?? 0) > 1 && <div className="sub" title={u.org_names!.join(", ")}>+ {u.org_names!.slice(1).join(", ")}</div>}{(u.client_org_names?.length ?? 0) > 0 && <div className="sub" title="Client companies this Super Admin manages">Manages {u.client_org_names!.join(", ")}</div>}</td>
                <td>{u.role ? ROLE_LABEL[u.role] : "—"}{u.super_admin && <span className="apill info sa-pill">Super Admin</span>}
                  {u.role === "skyward_admin" && !u.super_admin && u.support_view && <div className="sub">Support view allowed</div>}{u.group_scope.length > 0 && <div className="sub">{u.group_scope.map((g) => groups.find((x) => x.id === g)?.name).filter(Boolean).join(", ")} only</div>}</td>
                <td><StatusPill u={u} />
                  {u.must_change_password && u.status === "active" && <span className="sub"> temp pw</span>}
                  {notSignedIn(u) && u.access_email_at && <div className="sub">emailed {fmtDateTime(u.access_email_at)}</div>}</td>
                <td>{u.last_sign_in_at ? fmtDateTime(u.last_sign_in_at) : <span className="sub">Never</span>}</td>
                <td className="arow-act">{notSignedIn(u) && (
                  <button className="btn-ghost small" disabled={sending === u.id} onClick={(e) => { e.stopPropagation(); resend(u); }}>
                    {sending === u.id ? "Sending…" : "Re-send invite"}</button>)}</td>
              </tr>
            ))}
            {users && !rows.length && <tr className="empty"><td colSpan={7}>{pending ? "Everyone has signed in." : "No users found."}</td></tr>}
          </tbody>
        </table>
      </div>
      {edit === "new" && <AddUser orgs={orgs} groups={groups} onClose={() => setEdit(null)} onDone={done} />}
      {edit && edit !== "new" && <EditUser user={edit} orgs={orgs} groups={groups} onClose={() => setEdit(null)} onDone={done} />}
    </section>
  );
}

/** "Invite sent" (not accepted yet) or "Not signed in" (account ready, never used) or the normal status. */
export function StatusPill({ u }: { u: AdminUser }) {
  if (u.status === "invited") return <Pill value="invited" label="Invite sent" />;
  if (u.status === "active" && !u.last_sign_in_at) return <Pill value="invited" label="Not signed in" />;
  return <Pill value={u.status} />;
}

export const inviteSentText = (email: string, r: { sent: string }) => r.sent === "welcome"
  ? `Sign-up code emailed to ${email}. They enter it on the page the email links to, then choose their own password.`
  : `Invitation re-sent to ${email}. They enter the code from the email, then choose their own password.`;

export function TempPasswordNotice({ text, password, warn, onClose }: { text: string; password?: string; warn?: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Notice kind={password || warn ? "warn" : "ok"} onClose={onClose}>
      {text}
      {password && (
        <div className="temp-pw">
          Temporary password: <code>{password}</code>
          <button className="btn-ghost small" onClick={() => { navigator.clipboard.writeText(password); setCopied(true); }}>{copied ? "Copied" : "Copy"}</button>
          <div className="sub">Shown once only. Give it to the user by phone or in person — not by email. They must change it when they first sign in.</div>
        </div>
      )}
    </Notice>
  );
}

function RoleSelect({ org, value, onChange, locked }: { org: Org | undefined; value: string; onChange: (v: string) => void; locked?: boolean }) {
  const me = useAdminMe();
  const sup = !!me?.super_admin;
  const roles = org?.type === "internal" ? [...STAFF_ROLES, ...CLIENT_ROLES] : CLIENT_ROLES;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} required disabled={locked}>
      <option value="" disabled>Choose a role…</option>
      {roles.map((r) => <option key={r} value={r} disabled={r === "skyward_admin" && !sup && value !== r}>{ROLE_LABEL[r]}{r === "skyward_admin" && !sup ? " (Super Admin only)" : ""}</option>)}
    </select>
  );
}

const ROLE_HELP: Record<Role, string> = {
  skyward_admin: "Everything, including companies, users and this admin area.",
  skyward_tech: "Admin area for devices and SIM cards only. Sees all companies on the map.",
  org_owner: "Client manager: sees all of the company's devices. (Billing and user management come later.)",
  org_user: "Sees the company's devices on the map and in history.",
  viewer: "Read-only map access, e.g. a dispatcher.",
};

export function AddUser({ orgs, groups, onClose, onDone, defaultOrg }: { orgs: Org[]; groups: Group[]; onClose: () => void; onDone: (t: string, p?: string, warn?: boolean) => void; defaultOrg?: string }) {
  const [f, setF] = useState({ email: "", full_name: "", phone: "", role: "", method: "invite", password: "" });
  const [orgIds, setOrgIds] = useState<string[]>(defaultOrg ? [defaultOrg] : []);
  const [scope, setScope] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const org = orgIds.length === 1 ? orgs.find((o) => o.id === orgIds[0]) : undefined;

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (!orgIds.length) throw new Error("Choose a company");
      const body = { ...clean({ ...f, password: f.method === "password" ? f.password : "" }), org_ids: orgIds, group_scope: orgIds.length === 1 ? scope : [] };
      const r = await adminApi<{ email: string; temp_password: string | null; email_error?: string | null }>("POST", "users", body);
      if (r.email_error) return onDone(`Account created for ${r.email}, but the invitation email failed: ${r.email_error}. Open the user and use “Re-send invitation”.`, undefined, true);
      onDone(f.method === "invite"
        ? `Invitation emailed to ${r.email}. They'll get a code to set their own password.`
        : `Account created for ${r.email}.`, r.temp_password ?? undefined);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Drawer title="Add user" onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="user-form" disabled={busy}>{busy ? "Saving…" : f.method === "invite" ? "Send invite" : "Create account"}</button></>}>
      <form id="user-form" className="aform" onSubmit={save}>
        <Field label="Full name"><input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required maxLength={120} /></Field>
        <Field label="Email"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
        <Field label="Mobile"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} maxLength={40} placeholder="Optional" /></Field>
        <Field label="Company" hint={companyHint(orgIds, f.role)}>
          <CompanyMultiSelect orgs={orgs.filter((o) => o.status !== "closed")} value={orgIds} role={f.role}
            onChange={(ids) => { setOrgIds(ids); if (!sameKind(orgs, orgIds, ids)) setF({ ...f, role: "" }); setScope([]); }} />
        </Field>
        <Field label="Role" wide hint={f.role ? ROLE_HELP[f.role as Role] : undefined}>
          <RoleSelect org={org} value={f.role} onChange={(role) => setF({ ...f, role })} />
        </Field>
        {orgIds.length === 1 && <ScopePicker role={f.role} groups={groups.filter((g) => g.org_id === orgIds[0])} value={scope} onChange={setScope} />}
        <fieldset className="wide aradio">
          <legend>How will they get in?</legend>
          <label><input type="radio" checked={f.method === "invite"} onChange={() => setF({ ...f, method: "invite" })} />
            <span><b>Email invite</b> — they get a code by email and choose their own password. (Recommended)</span></label>
          <label><input type="radio" checked={f.method === "password"} onChange={() => setF({ ...f, method: "password" })} />
            <span><b>Temporary password</b> — you give it to them; they must change it at first sign-in.</span></label>
        </fieldset>
        {f.method === "password" && (
          <Field label="Temporary password" wide hint="Leave blank to generate one (e.g. reef-mango-4821). At least 10 characters.">
            <input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="off" minLength={10} />
          </Field>
        )}
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}

export function EditUser({ user, orgs, groups, onClose, onDone }: { user: AdminUser; orgs: Org[]; groups: Group[]; onClose: () => void; onDone: (t: string, p?: string, warn?: boolean) => void }) {
  const me = useAdminMe();
  const sup = !!me?.super_admin;
  const adminTarget = user.role === "skyward_admin" || !!user.super_admin;
  const lockedAdmin = adminTarget && !sup;
  const [f, setF] = useState({ full_name: user.full_name ?? "", phone: user.phone ?? "", role: user.role ?? "" });
  const [orgIds, setOrgIds] = useState<string[]>(user.org_ids ?? (user.org_id ? [user.org_id] : []));
  const [scope, setScope] = useState<string[]>(user.group_scope ?? []);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const org = orgIds.length === 1 ? orgs.find((o) => o.id === orgIds[0]) : undefined;

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label); setErr(null);
    try { await fn(); } catch (e: any) { setErr(e.message); } finally { setBusy(null); }
  };

  const save = (e: FormEvent) => { e.preventDefault(); run("save", async () => {
    if (!orgIds.length) throw new Error("Choose a company");
    await adminApi("PATCH", `users/${user.id}`, { ...clean(f), ...(user.is_me ? {} : { org_ids: orgIds, group_scope: orgIds.length === 1 ? scope : [] }) }); onDone(`Saved ${user.email}.`);
  }); };
  const waiting = notSignedIn(user);
  const sendEmail = () => run("email", async () => {
    if (waiting) {
      onDone(inviteSentText(user.email, await adminApi<{ sent: string }>("POST", `users/${user.id}/resend-invite`, {})));
      return;
    }
    const r = await adminApi<{ sent: string }>("POST", `users/${user.id}/reset`, { method: "email" });
    onDone(r.sent === "invite" ? `Invitation re-sent to ${user.email}.` : `Password-reset code emailed to ${user.email}. They enter it at Forgot password → I already have a code (or use the button in the email).`);
  });
  const tempPw = () => run("temp", async () => {
    if (!confirm(`Set a new temporary password for ${user.email}? Their current password will stop working.`)) return;
    const r = await adminApi<{ temp_password: string }>("POST", `users/${user.id}/reset`, { method: "password" });
    onDone(`Temporary password set for ${user.email}.`, r.temp_password);
  });
  const [clientIds, setClientIds] = useState<string[]>(user.client_org_ids ?? []);
  const clientDirty = JSON.stringify([...clientIds].sort()) !== JSON.stringify([...(user.client_org_ids ?? [])].sort());
  const saveClients = () => run("clients", async () => {
    await adminApi("PATCH", `users/${user.id}/client-access`, { org_ids: clientIds });
    onDone(`Saved the client companies ${user.is_me ? "you manage" : `${user.email} manages`}.`);
  });
  const setSupport = (v: boolean) => run("perm", async () => {
    await adminApi("PATCH", `users/${user.id}/permissions`, { support_view: v });
    onDone(`${v ? "Allowed" : "Removed"} Support view for ${user.email}.`);
  });
  const toggle = () => run("toggle", async () => {
    const off = user.status !== "disabled";
    if (off && !confirm(`Disable ${user.email}? They will be signed out within the hour and can't sign in again until re-enabled.`)) return;
    await adminApi("POST", `users/${user.id}/${off ? "disable" : "enable"}`, {});
    onDone(`${off ? "Disabled" : "Re-enabled"} ${user.email}.`);
  });
  const remove = () => run("delete", async () => {
    if (prompt(`This permanently deletes ${user.email}. Type DELETE to confirm.`) !== "DELETE") return;
    await adminApi("DELETE", `users/${user.id}`);
    onDone(`Deleted ${user.email}.`);
  });

  return (
    <Drawer title={user.full_name ?? user.email} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Close</button>
        <button className="btn-primary" form="user-edit" disabled={!!busy}>{busy === "save" ? "Saving…" : "Save changes"}</button></>}>
      <form id="user-edit" className="aform" onSubmit={save}>
        <div className="wide kv">
          <span>Email</span><b className="mono">{user.email}</b>
          <span>Status</span><span><StatusPill u={user} /></span>
          <span>Last sign-in</span><span>{user.last_sign_in_at ? fmtDateTime(user.last_sign_in_at) : "Never"}</span>
          {user.access_email_at && <><span>Last account email</span><span>{fmtDateTime(user.access_email_at)} · {ACCESS_KIND[user.access_email_kind ?? ""] ?? "email"}</span></>}
        </div>
        <Field label="Full name"><input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required maxLength={120} /></Field>
        <Field label="Mobile"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} maxLength={40} /></Field>
        <Field label="Company" hint={user.is_me ? "You can't change your own company." : companyHint(orgIds, f.role)}>
          <CompanyMultiSelect orgs={orgs} value={orgIds} role={f.role} disabled={user.is_me || lockedAdmin}
            onChange={(ids) => { setOrgIds(ids); if (!sameKind(orgs, orgIds, ids)) setF({ ...f, role: "" }); setScope([]); }} />
        </Field>
        <Field label="Role" hint={user.is_me ? "You can't change your own role." : f.role ? ROLE_HELP[f.role as Role] : undefined}>
          {user.is_me ? <input value={user.role ? ROLE_LABEL[user.role] : ""} disabled /> :
            <RoleSelect org={org} value={f.role} onChange={(role) => setF({ ...f, role })} locked={lockedAdmin || !!user.super_admin} />}
        </Field>
        {!user.is_me && orgIds.length === 1 && <ScopePicker role={f.role} groups={groups.filter((g) => g.org_id === orgIds[0])} value={scope} onChange={setScope} />}
        {err && <div className="error wide">{err}</div>}
      </form>

      <div className="aactions">
        <h3>Sign-in help</h3>
        <button className="btn-ghost" onClick={sendEmail} disabled={!!busy}>
          {busy === "email" ? "Sending…" : waiting ? "Re-send invitation" : "Email a password-reset code"}
        </button>
        <button className="btn-ghost" onClick={tempPw} disabled={!!busy}>{busy === "temp" ? "Setting…" : "Set a temporary password"}</button>
        {sup && user.role === "skyward_admin" && !user.super_admin && <>
          <h3>Support view</h3>
          <label className="acheck"><input type="checkbox" checked={!!user.support_view} disabled={!!busy} onChange={(e) => setSupport(e.target.checked)} />
            Allowed to open Support view (see a client company's live data for 1–8 h, logged)</label>
        </>}
        {sup && user.super_admin && <>
          <h3>Client companies {user.is_me ? "I manage" : "they manage"}</h3>
          <p className="sub">Owner access to these companies (map, zones, alerts, Company page, fleet, reports) with no Support view. Other client companies still need a Support view.</p>
          <div className="client-access">
            <CompanyMultiSelect orgs={orgs.filter((o) => o.type !== "internal")} value={clientIds} role="org_owner" disabled={!!busy} onChange={setClientIds} />
            <button className="btn-ghost" onClick={saveClients} disabled={!!busy || !clientDirty}>{busy === "clients" ? "Saving…" : "Save companies"}</button>
          </div>
        </>}
        {user.super_admin && <p className="sub">Super Admin — manages Skyward admins and Support view. Can't be disabled or deleted here.</p>}
        {!user.is_me && !user.super_admin && !lockedAdmin && <>
          <h3>Access</h3>
          <button className="btn-ghost" onClick={toggle} disabled={!!busy}>{user.status === "disabled" ? "Re-enable account" : "Disable account"}</button>
          <button className="btn-ghost danger" onClick={remove} disabled={!!busy}>Delete user</button>
        </>}
      </div>
    </Drawer>
  );
}

const ACCESS_KIND: Record<string, string> = { invite: "invitation", welcome: "sign-up code", reset: "reset code" };

/** For company users/viewers: see the whole company (default) or only some device groups. */
function ScopePicker({ role, groups, value, onChange }: { role: string; groups: Group[]; value: string[]; onChange: (v: string[]) => void }) {
  if (!["org_user", "viewer"].includes(role) || !groups.length) return null;
  const limited = value.length > 0;
  return (
    <fieldset className="wide aradio">
      <legend>Which vehicles can they see?</legend>
      <label><input type="radio" checked={!limited} onChange={() => onChange([])} /><span><b>All</b> of the company's devices</span></label>
      <label><input type="radio" checked={limited} onChange={() => onChange([groups[0].id])} /><span><b>Only these groups:</b></span></label>
      {limited && (
        <div className="scope-list">
          {groups.map((g) => (
            <label key={g.id}><input type="checkbox" checked={value.includes(g.id)}
              onChange={(e) => onChange(e.target.checked ? [...value, g.id] : value.filter((x) => x !== g.id).length ? value.filter((x) => x !== g.id) : value)} />{g.name}</label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------- V3.10.2: several companies per user
const isStaffRole = (r: string) => (STAFF_ROLES as string[]).includes(r);
/** Did the choice switch between Skyward's own company and client companies (then the role must be picked again)? */
function sameKind(orgs: Org[], a: string[], b: string[]) {
  const internal = (ids: string[]) => ids.some((id) => orgs.find((o) => o.id === id)?.type === "internal");
  return internal(a) === internal(b) && (a.length > 0) === (b.length > 0);
}
const companyHint = (ids: string[], role: string) =>
  ids.length > 1 ? `${ids.length} companies — they see all of them, with the same role in each. Group limits need a single company.`
  : isStaffRole(role) ? "Skyward staff roles: Skyward's own company only." : "Tick more than one company for owners who manage several.";

/** Drop-down list with a tick box per company. Skyward's own company can't be combined with client companies, and a
 *  Skyward staff role allows one company only. */
export function CompanyMultiSelect({ orgs, value, onChange, role, disabled }: { orgs: Org[]; value: string[]; onChange: (ids: string[]) => void; role: string; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc, true);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc, true); };
  }, [open]);
  const names = value.map((id) => orgs.find((o) => o.id === id)?.name ?? "Unknown company");
  const single = isStaffRole(role);
  const shown = orgs.filter((o) => !q.trim() || o.name.toLowerCase().includes(q.trim().toLowerCase()));
  function toggle(o: Org) {
    const on = value.includes(o.id);
    if (on) return onChange(value.filter((x) => x !== o.id));
    const internal = o.type === "internal";
    const keep = value.filter((id) => (orgs.find((x) => x.id === id)?.type === "internal") === internal);
    onChange(single || internal ? [o.id] : [...keep, o.id]);
    if (single || internal) setOpen(false);
  }
  return (
    <div className={`cms ${open ? "open" : ""}`} ref={ref}>
      <button type="button" className="cms-btn" onClick={() => setOpen(!open)} disabled={disabled} aria-haspopup="listbox" aria-expanded={open}>
        <span className={names.length ? "" : "sub"}>{names.length === 0 ? "Choose a company…" : names.length === 1 ? names[0] : `${names[0]} + ${names.length - 1} more`}</span>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {names.length > 1 && <div className="cms-chips">{value.map((id, i) => (
        <span key={id} className="cms-chip">{names[i]}{!disabled && <button type="button" aria-label={`Remove ${names[i]}`} onClick={() => onChange(value.filter((x) => x !== id))}>×</button>}</span>
      ))}</div>}
      {open && (
        <div className="cms-pop" role="listbox" aria-multiselectable={!single}>
          {orgs.length > 8 && <input className="cms-q" placeholder="Search companies" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />}
          {shown.map((o) => (
            <label key={o.id} className="cms-opt" role="option" aria-selected={value.includes(o.id)}>
              <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o)} />
              <span>{o.name}</span>{o.type === "internal" && <span className="sub">Skyward · on its own</span>}
            </label>
          ))}
          {!shown.length && <div className="sub cms-none">No company matches.</div>}
        </div>
      )}
    </div>
  );
}
